/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import type { Theme, ThemeType } from "@packages/ui";
import {
  ColorIcon,
  constantSemanticColors,
  cssMediaQueryMinWidthTablet,
  DropdownMenu,
  DropdownMenuHeader,
  DropdownMenuItem,
  IconButton,
  Modal,
  ModalHeader,
  ModeIcon,
  numericPercentages,
  radius,
  spacing,
  stroke,
  TabGroup,
  TabGroupItem,
  Text,
  TextSizeIcon,
  Toggle,
  useIsDesktopViewport,
  useTheme,
  zIndices,
} from "@packages/ui";
import { useRef, useState } from "react";

const containerStyle: Interpolation = {
  alignItems: "center",
  border: `${stroke.regular} solid ${constantSemanticColors.fixedLightenLowest}`,
  borderRadius: radius.m,
  columnGap: spacing.m,
  display: "grid",
  gridTemplateAreas: '"icon text toggle"',
  gridTemplateColumns: "min-content 1fr min-content",
  gridTemplateRows: "1fr",
  padding: spacing.m,
};
const toggleTextStyle: Interpolation = {
  alignItems: "flex-start",
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
  flexWrap: "nowrap",
  justifyContent: "flex-start",
  rowGap: spacing.t,
};

const menuItemContainerStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  flexWrap: "nowrap",
  justifyContent: "center",
  width: numericPercentages[100],
};
const menuItemStyle: Interpolation = {
  alignItems: "center",
  borderRadius: radius.m,
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
  flexShrink: 1,
  height: "max-content",
  justifyContent: "center",
  minWidth: "revert",
  padding: `${spacing.m} 0`,
  rowGap: spacing.s,
};
const menuHeaderStyle: Interpolation = {
  marginTop: spacing.m,
};

const defaultSettingsStyle: Interpolation = {
  opacity: 1,
  pointerEvents: "auto",
  position: "fixed",
  right: spacing.m,
  [cssMediaQueryMinWidthTablet]: { right: "80px", top: "80px" },
  top: spacing.m,
  transition: "opacity 250ms ease-out",
  willChange: "opacity",
  zIndex: zIndices.maskOver,
};

const darkStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.primary,
});

const itemStyle: Interpolation = {
  justifyContent: "flex-start",
};
const textStyle: Interpolation = {
  flexGrow: 1,
};

const defaultDropdownStyle: Interpolation = {
  zIndex: zIndices.modalOverTranscript,
};

export type TextSettingsProps = {
  isHeaderCollapsed: boolean;
  immersive: "immersive" | "default";
  fontSize: "s" | "m" | "l";
  setFontSize: (f: "s" | "m" | "l") => void;
  setImmersive: (i: "immersive" | "default") => void;
};

export const OldTextSettings = ({
  immersive,
  fontSize,
  setFontSize,
  setImmersive,
}: TextSettingsProps) => {
  const onDesktop = useIsDesktopViewport();
  const t = useTranslations();
  const [open, setOpen] = useState<boolean>(false);
  const { switchTheme: setEmotionTheme, currentTheme } = useTheme();
  const originalEmotionTheme = useRef<Theme>(currentTheme);

  const menuTrigger = (
    <IconButton
      icon={<TextSizeIcon.Small />}
      variant={"transparentPrimary"}
      css={defaultSettingsStyle}
    />
  );

  if (onDesktop) {
    return (
      <DropdownMenu
        trigger={menuTrigger}
        contentCSS={[
          immersive === "immersive" ? darkStyle : null,
          defaultDropdownStyle,
        ]}
        align={"end"}
      >
        <DropdownMenuHeader key={"tosSettingsHeader"}>
          {t("text_on_screen_text_options")}
        </DropdownMenuHeader>
        <DropdownMenuItem
          key={"tosFontS"}
          startIcon={<TextSizeIcon.Small />}
          selected={fontSize === "s"}
          onSelect={() => setFontSize("s")}
          css={itemStyle}
          asChild
        >
          <div>
            <span css={textStyle}>
              {t("transcript_options_dialog_text_size_small")}
            </span>
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem
          key={"tosFontM"}
          startIcon={<TextSizeIcon.Medium />}
          selected={fontSize === "m"}
          onSelect={() => setFontSize("m")}
          css={itemStyle}
          asChild
        >
          <div>
            <span css={textStyle}>
              {t("transcript_options_dialog_text_size_medium")}
            </span>
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem
          key={"tosFontL"}
          startIcon={<TextSizeIcon.Large />}
          selected={fontSize === "l"}
          onSelect={() => setFontSize("l")}
          css={itemStyle}
          asChild
        >
          <div>
            <span css={textStyle}>
              {t("transcript_options_dialog_text_size_large")}
            </span>
          </div>
        </DropdownMenuItem>
        <div key={"tosImmersion"} css={containerStyle}>
          <ModeIcon color={constantSemanticColors.onOverlayDarkMed} />
          <div css={toggleTextStyle}>
            <Text key={"immersionModeTitle"} type={"title"} size={"m"}>
              {t("text_on_screen_immersion_mode")}
            </Text>
            <Text key={"immersionModeExplanation"} type={"detail"} size={"xl"}>
              {t("text_on_screen_immersion_mode_explanation")}
            </Text>
          </div>
          <Toggle
            pressed={immersive === "immersive"}
            onPressedChange={(b) => {
              setImmersive(b ? "immersive" : "default");
              if (b) setEmotionTheme("dark");
              else setEmotionTheme(originalEmotionTheme.current);
            }}
          />
        </div>
      </DropdownMenu>
    );
  }

  return (
    <Modal
      trigger={menuTrigger}
      contentCSS={[immersive ? darkStyle : null]}
      open={open}
      onOpenChange={setOpen}
      overlayProps={{ onClick: () => setOpen(false) }}
    >
      <ModalHeader>{t("transcript_options_dialog_text_size")}</ModalHeader>
      <TabGroup
        type={"single"}
        value={fontSize}
        css={menuItemContainerStyle}
        onValueChange={setFontSize}
      >
        <TabGroupItem css={menuItemStyle} value={"s"} parentState={fontSize}>
          <>
            <TextSizeIcon.Small />
            <Text type={"button"} size={"s"}>
              {t("transcript_options_dialog_text_size_small")}
            </Text>
          </>
        </TabGroupItem>
        <TabGroupItem
          css={menuItemStyle}
          key={"tosFontM"}
          value={"m"}
          parentState={fontSize}
        >
          <>
            <TextSizeIcon.Medium />
            <Text type={"button"} size={"s"}>
              {t("transcript_options_dialog_text_size_medium")}
            </Text>
          </>
        </TabGroupItem>
        <TabGroupItem
          css={menuItemStyle}
          key={"tosFontL"}
          value={"l"}
          parentState={fontSize}
        >
          <>
            <TextSizeIcon.Large />
            <Text type={"button"} size={"s"}>
              {t("transcript_options_dialog_text_size_large")}
            </Text>
          </>
        </TabGroupItem>
      </TabGroup>
      <ModalHeader css={menuHeaderStyle}>
        {t("transcript_options_dialog_background")}
      </ModalHeader>
      <TabGroup
        type={"single"}
        value={immersive}
        onValueChange={setImmersive}
        css={menuItemContainerStyle}
      >
        <TabGroupItem
          key={"tosImmersive"}
          css={menuItemStyle}
          value={"immersive"}
          parentState={immersive}
        >
          <>
            <ColorIcon />
            <Text type={"button"} size={"s"}>
              {t("transcript_options_dialog_background_color")}
            </Text>
          </>
        </TabGroupItem>
        <TabGroupItem
          key={"tosDefaultBg"}
          css={menuItemStyle}
          value={"default"}
          parentState={immersive}
        >
          <>
            <ModeIcon />
            <Text type={"button"} size={"s"}>
              {t("transcript_options_dialog_background_theme")}
            </Text>
          </>
        </TabGroupItem>
      </TabGroup>
    </Modal>
  );
};
